/**
 * Client mount harness. Every site's main.tsx ended with the same dance:
 * server-rendered markup (root has children) → hydrate; empty root (dev / a
 * standalone build) → fresh render. Fonts and globals.css stay in the site's
 * main.tsx (they are per-design); the site imports them, then calls mountClient.
 *
 *   import './globals.css'
 *   import App from './App'
 *   import { mountClient } from '@gjl/site-kit'
 *   mountClient(App)
 */
import { StrictMode, type ComponentType } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { initGA4 } from '@gjl/analytics'
import type { InjectedCmsData } from './cms/types'
import { PlatformShell, type PlatformOptions } from './platform-routes'
import { ScrollReset } from './scroll-reset'

export interface MountOptions {
  rootId?: string
  /** Opt into platform routes (client galleries, …) injected ahead of the site. */
  platform?: PlatformOptions
  /**
   * Set false to skip the publish-configured GA4 init (see `mountAnalytics`).
   * For a site that wires its own analytics — e.g. behind `mountConsentedAnalytics`
   * — so the two do not both call `gtag('config', …)`.
   */
  analytics?: boolean
}

/**
 * Initialise GA4 from the measurement id the publish baked into the bundle.
 *
 * WHY THIS LIVES IN THE MOUNT HARNESS, like ScrollReset above it: it is not a
 * design decision any site should be able to forget, and 35 of 36 client sites
 * did forget it. `ga4MeasurementId` has been editable in the app (Website →
 * Settings → Google Analytics 4) and injected into `window.__CMS_DATA__` on every
 * publish for months, while exactly ONE site — demo-great-host-composers — read
 * it. Everywhere else a client could paste their measurement id, publish, and
 * collect nothing, with no error and no way to tell.
 *
 * NOT consent-gated, preserving the owner decision recorded in that site's
 * main.tsx on 2026-07-27: these sites track every visitor with no cookie banner.
 * That decision is now in one place instead of one site. Consent-gating without
 * also rendering `<CookieConsent>` would be worse than the bug being fixed —
 * analytics would initialise never rather than always, and look wired either way.
 * A site that wants the gate passes `analytics: false` and calls
 * `mountConsentedAnalytics` itself.
 *
 * The id is baked at PUBLISH time: saving the setting changes nothing until the
 * next publish. `initGA4` no-ops on a falsy id, so local dev and any org that has
 * not set one stay untracked.
 */
function mountAnalytics(): void {
  if (typeof window === 'undefined') return
  const injected = (window as { __CMS_DATA__?: InjectedCmsData }).__CMS_DATA__
  initGA4(injected?.siteConfig?.ga4MeasurementId)
}

/**
 * Mount a site's App. `opts` is backward-compatible: a bare string is the legacy
 * rootId; an object is { rootId?, platform? }. Passing `platform` injects the
 * shared platform route table (e.g. /g/:token) so the site needn't hardcode it.
 */
export function mountClient(App: ComponentType, opts: string | MountOptions = {}): void {
  const {
    rootId = 'root',
    platform,
    analytics = true,
  } = typeof opts === 'string' ? { rootId: opts, platform: undefined, analytics: true } : opts
  const el = document.getElementById(rootId)
  if (!el) throw new Error(`mountClient: #${rootId} element not found`)

  if (analytics) mountAnalytics()

  const appEl = platform ? <PlatformShell appElement={<App />} platform={platform} /> : <App />
  // ScrollReset lives here rather than in each site's App: it needs router
  // context (so it cannot wrap BrowserRouter) and it is not a design decision
  // any site should be able to forget. See scroll-reset.tsx.
  const tree = (
    <StrictMode>
      <BrowserRouter>
        <ScrollReset />
        {appEl}
      </BrowserRouter>
    </StrictMode>
  )

  if (el.hasChildNodes()) {
    hydrateRoot(el, tree)
  } else {
    createRoot(el).render(tree)
  }
}
